/**
 * Single source of truth for consumer-tier entitlements.
 * Mirrored verbatim in `supabase/functions/_shared/entitlements-matrix.ts`
 * for server-side checks. A unit test asserts deep equality so the two
 * copies cannot drift.
 */

export type ConsumerTierCode = "free" | "founder" | "operator" | "team";

export interface Entitlements {
  /** null = unlimited. Free tier is capped at 5 messages / UTC day. */
  koraDailyMessageLimit: number | null;
  koraPriority: boolean;
  velyraEnabled: boolean;
  selfPacedLibrary: "samples" | "full";
  community: "read" | "full";
  officeHours: "none" | "group" | "facilitated_group";
  recordings: boolean;
  certificates: boolean;
  resourceLibrary: "limited3" | "unlimited";
  premiumPlaybooks: boolean;
  /** 0 or 15. Applied only to platform-delivered, discount-eligible products. */
  platformDiscountPct: number;
  seats: number;
}

export const PLAN_ENTITLEMENTS: Record<ConsumerTierCode, Entitlements> = {
  free:     { koraDailyMessageLimit: 5,    koraPriority: false, velyraEnabled: false, selfPacedLibrary: "samples", community: "read", officeHours: "none",              recordings: false, certificates: false, resourceLibrary: "limited3",  premiumPlaybooks: false, platformDiscountPct: 0,  seats: 1 },
  founder:  { koraDailyMessageLimit: null, koraPriority: false, velyraEnabled: false, selfPacedLibrary: "full",    community: "full", officeHours: "group",             recordings: true,  certificates: true,  resourceLibrary: "unlimited", premiumPlaybooks: false, platformDiscountPct: 0,  seats: 1 },
  operator: { koraDailyMessageLimit: null, koraPriority: true,  velyraEnabled: true,  selfPacedLibrary: "full",    community: "full", officeHours: "facilitated_group", recordings: true,  certificates: true,  resourceLibrary: "unlimited", premiumPlaybooks: true,  platformDiscountPct: 15, seats: 1 },
  team:     { koraDailyMessageLimit: null, koraPriority: true,  velyraEnabled: true,  selfPacedLibrary: "full",    community: "full", officeHours: "facilitated_group", recordings: true,  certificates: true,  resourceLibrary: "unlimited", premiumPlaybooks: true,  platformDiscountPct: 15, seats: 5 },
};

/**
 * Pricing for the four consumer tiers, in EUR minor units (cents).
 * Annual is monthly * 12 * 0.79, rounded. Provider price IDs live in env.
 */
export const CONSUMER_PLANS = {
  free:     { code: "free",     monthly: 0,     annual: 0      },
  founder:  { code: "founder",  monthly: 1900,  annual: 18000  }, // €19 / €180
  operator: { code: "operator", monthly: 4900,  annual: 46500  }, // €49 / €465
  team:     { code: "team",     monthly: 14900, annual: 141300, seatsIncluded: 5 }, // €149 / €1,413
} as const;

/** BCEAO fixed peg. EUR↔FCFA never floats — never derive from fx_rates. */
export const EUR_TO_FCFA = 655.957;

export function formatFCFA(eurCents: number): string {
  const fcfa = Math.round((eurCents / 100) * EUR_TO_FCFA);
  return `${fcfa.toLocaleString("fr-FR").replace(/,/g, " ")} FCFA`;
}

/** Annual savings (as %) for a given tier. */
export function annualSavingsPct(tier: ConsumerTierCode): number {
  const p = CONSUMER_PLANS[tier] as { monthly: number; annual: number };
  if (!p.monthly || !p.annual) return 0;
  return Math.round((1 - p.annual / (p.monthly * 12)) * 100);
}